import type { GameContext } from "../core/GameContext";
import { MoveEntityCommand } from "../core/commands/MoveEntityCommand";
import { NavigateCommand } from "../core/commands/NavigateCommand";
import { RestartCommand } from "../core/commands/RestartCommand";
import { DiscoverArtifactCommand } from "../core/commands/DiscoverArtifactCommand";
import { ExamineArtifactCommand } from "../core/commands/ExamineArtifactCommand";
import type { InteractionContext } from "../interaction/Interaction";
import { getComponent, PLAYER_CHARACTER_ID as PLAYER_ID, type Entity } from "../world/Entity";
import type { Vec2 } from "../world/geometry";
import { findItem } from "../inventory/Inventory";

const DEFAULT_NEARBY_RADIUS = 300;

/** Lets GameBridge walk the player smoothly instead of teleporting, reusing game.ts's click-to-walk movement. */
export interface PlayerMotion {
  walkTo(pos: Vec2): Promise<Vec2>;
}

export interface SceneResponse {
  readonly sceneId: string;
  readonly title?: string;
  readonly size: { readonly width: number; readonly height: number };
  readonly player: { readonly x: number; readonly y: number; readonly facing: string };
  readonly entities: readonly EntitySummary[];
  readonly portals: readonly PortalSummary[];
}

export interface EntitySummary {
  readonly id: string;
  readonly kind: string;
  readonly definitionId: string;
  readonly x: number;
  readonly y: number;
  readonly description?: string;
  readonly actions?: readonly string[];
  readonly discovered?: boolean;
}

export interface PortalSummary {
  readonly id: string;
  readonly toScene: string;
  readonly x: number;
  readonly y: number;
}

export interface EntityDetail {
  readonly id: string;
  readonly kind: string;
  readonly definitionId: string;
  readonly position: { readonly x: number; readonly y: number };
  readonly facing: string;
  readonly visible: boolean;
  readonly description?: string;
  readonly actions: readonly string[];
  readonly components: readonly string[];
}

export interface InventoryResponse {
  readonly items: readonly InventoryItemSummary[];
}

export interface InventoryItemSummary {
  readonly instanceId: string;
  readonly definitionId: string;
  readonly category: string;
  readonly quantity: number;
  readonly stackable: boolean;
}

export interface KnowledgeResponse {
  readonly progress: Readonly<Record<string, string>>;
}

export interface ToolResult {
  readonly content: readonly { readonly type: "text"; readonly text: string }[];
}

function ok(data: unknown): ToolResult {
  return { content: [{ type: "text", text: JSON.stringify(data) }] };
}

function fail(message: string): ToolResult {
  return { content: [{ type: "text", text: message }] };
}

function distance(a: Vec2, b: Vec2): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return Math.sqrt(dx * dx + dy * dy);
}

function buildSummary(e: Entity): EntitySummary {
  const inspectable = getComponent(e, "inspectable");
  const interactable = getComponent(e, "interactable");
  const artifact = getComponent(e, "artifact");
  return {
    id: e.id,
    kind: e.kind,
    definitionId: e.definitionId,
    x: e.transform.position.x,
    y: e.transform.position.y,
    ...(inspectable ? { description: inspectable.description } : {}),
    ...(interactable ? { actions: interactable.interactions } : {}),
    ...(artifact ? { discovered: artifact.discovered } : {}),
  };
}

export class GameBridge {
  constructor(
    private readonly context: GameContext,
    private readonly motion: PlayerMotion,
  ) {}

  getScene(): SceneResponse {
    const scene = this.context.world.getCurrentScene();
    const player = this.context.world.getEntity(PLAYER_ID);
    const entities: EntitySummary[] = [];
    const portals: PortalSummary[] = [];

    for (const e of scene.entities) {
      if (!e.visible && e.id !== PLAYER_ID) continue;
      if (e.id === PLAYER_ID) continue;

      const portal = getComponent(e, "portal");
      if (portal) {
        portals.push({
          id: e.id,
          toScene: portal.toScene,
          x: e.transform.position.x,
          y: e.transform.position.y,
        });
      }

      entities.push(buildSummary(e));
    }

    const response: SceneResponse = {
      sceneId: scene.id,
      size: scene.definition.size,
      player: {
        x: player?.transform.position.x ?? 0,
        y: player?.transform.position.y ?? 0,
        facing: player?.transform.facing ?? "down",
      },
      entities,
      portals,
      ...(scene.definition.title ? { title: scene.definition.title } : {}),
    };
    return response;
  }

  getEntity(entityId: string): EntityDetail | undefined {
    const entity = this.context.world.getEntity(entityId);
    if (!entity) return undefined;
    const inspectable = getComponent(entity, "inspectable");
    const interactable = getComponent(entity, "interactable");
    return {
      id: entity.id,
      kind: entity.kind,
      definitionId: entity.definitionId,
      position: entity.transform.position,
      facing: entity.transform.facing,
      visible: entity.visible,
      actions: interactable?.interactions ?? [],
      components: entity.components.map((c) => c.type),
      ...(inspectable ? { description: inspectable.description } : {}),
    };
  }

  getInventory(): InventoryResponse {
    return {
      items: this.context.world.inventory.items.map((item) => ({
        instanceId: item.instanceId,
        definitionId: item.definitionId,
        category: item.category,
        quantity: item.quantity,
        stackable: item.stackable,
      })),
    };
  }

  getKnowledge(): KnowledgeResponse {
    return { progress: this.context.knowledge.getProgress() };
  }

  getNearby(radius = DEFAULT_NEARBY_RADIUS): readonly EntitySummary[] {
    const player = this.context.world.getEntity(PLAYER_ID);
    if (!player) return [];
    const scene = this.context.world.getCurrentScene();
    const pos = player.transform.position;

    const nearby: (EntitySummary & { dist: number })[] = [];
    for (const e of scene.entities) {
      if (e.id === PLAYER_ID || !e.visible) continue;
      const dist = distance(pos, e.transform.position);
      if (dist > radius) continue;
      nearby.push({ ...buildSummary(e), dist });
    }

    nearby.sort((a, b) => a.dist - b.dist);
    return nearby.map(({ dist: _, ...rest }) => rest);
  }

  /** Walks the player there at normal speed (see PlayerMotion), resolving once they arrive or the walk is interrupted. */
  moveTo(pos: Vec2): Promise<Vec2> {
    return this.motion.walkTo(pos);
  }

  moveEntity(entityId: string, pos: Vec2): ToolResult {
    const entity = this.context.world.getEntity(entityId);
    if (!entity) return fail(`Entity "${entityId}" not found`);
    this.context.commands.execute(new MoveEntityCommand(entityId, pos));
    return ok({ moved: true, position: pos });
  }

  private interactionContext(entity: Entity): InteractionContext {
    return { world: this.context.world, commands: this.context.commands, sfx: this.context.sfx, artifacts: this.context.artifacts, entity };
  }

  private baseContext(): InteractionContext {
    return { world: this.context.world, commands: this.context.commands, sfx: this.context.sfx, artifacts: this.context.artifacts };
  }

  async take(entityId: string): Promise<ToolResult> {
    const entity = this.context.world.getEntity(entityId);
    if (!entity) return fail(`Entity "${entityId}" not found`);
    const interaction = this.context.interactions.get("take");
    if (!interaction) return fail("Take interaction unavailable");
    const ctx = this.interactionContext(entity);
    if (!interaction.canExecute(ctx)) return fail(`Cannot take "${entityId}"`);
    const result = await interaction.execute(ctx);
    return result.success ? ok({ taken: entityId }) : fail(result.message ?? "Take failed");
  }

  async drop(instanceId: string, pos: Vec2): Promise<ToolResult> {
    const interaction = this.context.interactions.get("drop");
    if (!interaction) return fail("Drop interaction unavailable");
    const ctx: InteractionContext = { ...this.baseContext(), instanceId, position: pos };
    if (!interaction.canExecute(ctx)) return fail(`Cannot drop item "${instanceId}"`);
    const result = await interaction.execute(ctx);
    return result.success ? ok({ dropped: instanceId, position: pos }) : fail(result.message ?? "Drop failed");
  }

  async interact(entityId: string, action: string): Promise<ToolResult> {
    const interaction = this.context.interactions.get(action);
    if (!interaction) return fail(`Unknown action "${action}"`);

    const entity = this.context.world.getEntity(entityId);
    const ctx = entity
      ? this.interactionContext(entity)
      : (() => {
          const item = findItem(this.context.world.inventory, entityId);
          return item ? { ...this.baseContext(), instanceId: item.instanceId, inventoryItem: item } : undefined;
        })();
    if (!ctx) return fail(`Entity "${entityId}" not found`);

    if (!interaction.canExecute(ctx)) return fail(`Cannot "${action}" on "${entityId}"`);
    const result = await interaction.execute(ctx);
    return result.success
      ? ok({ action, entityId, message: result.message })
      : fail(result.message ?? `${action} failed`);
  }

  navigate(portalId: string): ToolResult {
    const entity = this.context.world.getEntity(portalId);
    if (!entity) return fail(`Entity "${portalId}" not found`);
    const portal = getComponent(entity, "portal");
    if (!portal) return fail(`"${portalId}" is not a portal`);

    this.context.commands.execute(new NavigateCommand(portalId));
    return ok({ navigated: portal.toScene });
  }

  async equip(instanceId: string): Promise<ToolResult> {
    const interaction = this.context.interactions.get("wear");
    if (!interaction) return fail("Wear interaction unavailable");
    const ctx: InteractionContext = { ...this.baseContext(), instanceId, actorId: PLAYER_ID };
    if (!interaction.canExecute(ctx)) return fail(`Cannot wear item "${instanceId}"`);
    const result = await interaction.execute(ctx);
    return result.success ? ok({ equipped: instanceId }) : fail(result.message ?? "Equip failed");
  }

  async unequip(slot: string): Promise<ToolResult> {
    const interaction = this.context.interactions.get("remove");
    if (!interaction) return fail("Remove interaction unavailable");
    const ctx: InteractionContext = { ...this.baseContext(), actorId: PLAYER_ID, slot: slot as "hat" };
    if (!interaction.canExecute(ctx)) return fail(`Cannot remove from slot "${slot}"`);
    const result = await interaction.execute(ctx);
    return result.success ? ok({ unequipped: slot }) : fail(result.message ?? "Unequip failed");
  }

  restartGame(): ToolResult {
    this.context.commands.execute(new RestartCommand());
    return ok({ restarted: true });
  }

  private existsAsEntityOrItem(entityId: string): boolean {
    return this.context.world.getEntity(entityId) !== undefined || findItem(this.context.world.inventory, entityId) !== undefined;
  }

  discover(entityId: string): ToolResult {
    if (!this.existsAsEntityOrItem(entityId)) return fail(`Entity "${entityId}" not found`);
    this.context.commands.execute(new DiscoverArtifactCommand(entityId));
    return ok({ discovered: entityId });
  }

  examine(entityId: string): ToolResult {
    if (!this.existsAsEntityOrItem(entityId)) return fail(`Entity "${entityId}" not found`);
    this.context.commands.execute(new ExamineArtifactCommand(entityId));
    return ok({ examined: entityId });
  }
}
